import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading, KPI, EmptyState } from "../components/UI";
import { ClipboardCheck, Plus, X, Download, FileText, Camera } from "lucide-react";
import SignaturePad from "../components/SignaturePad";

export default function Inspections() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [type, setType] = useState("move_in");
  const [tenantSig, setTenantSig] = useState("");
  const [managerSig, setManagerSig] = useState("");

  const reload = async () => {
    const [i, t, u] = await Promise.all([
      api.get("/inspections"),
      api.get("/tenants").catch(()=>({data:[]})),
      api.get("/units").catch(()=>({data:[]})),
    ]);
    setItems(i.data); setTenants(t.data); setUnits(u.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      type, // move_in / move_out
      tenant_id: f.get("tenant_id"),
      unit_id: f.get("unit_id"),
      inspection_date: f.get("inspection_date"),
      overall_condition: f.get("condition"),
      findings: f.get("findings"),
      meter_readings: f.get("meter_readings"),
      damages: f.get("damages"),
      keys_handed_over: f.get("keys") || "",
      repair_cost: Number(f.get("repair_cost") || 0),
      caution_deduction: Number(f.get("caution_deduction") || 0),
      refund_amount: Number(f.get("refund_amount") || 0),
      notes: f.get("notes"),
      tenant_signature_url: tenantSig,
      manager_signature_url: managerSig,
    };
    try {
      await api.post("/inspections", payload);
      toast.success("Inspection saved");
      setCreating(false); setTenantSig(""); setManagerSig("");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const moveIns = items.filter(i => i.type === "move_in");
  const moveOuts = items.filter(i => i.type === "move_out");

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Inspections</h1>
          <p className="text-[#5E7166] text-sm">Move-in & move-out condition reports.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-inspection-btn"><Plus size={16}/> New Inspection</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI label="Move-In" value={moveIns.length} icon={ClipboardCheck}/>
        <KPI label="Move-Out" value={moveOuts.length} icon={ClipboardCheck}/>
        <KPI label="Total" value={items.length} icon={FileText}/>
      </div>

      <Card title="All Inspections">
        {items.length === 0 ? <EmptyState icon={ClipboardCheck} title="No inspections yet" description="Create your first move-in inspection."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Type</th><th>Date</th><th>Unit</th><th>Tenant</th><th>Condition</th><th>Refund</th><th></th></tr></thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id} data-testid={`inspection-${i.id}`}>
                    <td><span className={i.type === "move_in" ? "badge badge-green" : "badge badge-gold"}>{statusLabel(i.type)}</span></td>
                    <td>{formatDate(i.inspection_date)}</td>
                    <td>{units.find(u => u.id === i.unit_id)?.unit_name || "—"}</td>
                    <td>{tenants.find(t => t.id === i.tenant_id)?.full_name || "—"}</td>
                    <td>{i.overall_condition || "—"}</td>
                    <td>{i.refund_amount ? naira(i.refund_amount) : "—"}</td>
                    <td><a href={`${API_BASE}/inspections/${i.id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline-emerald text-xs"><Download size={12}/> PDF</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="inspection-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">New {type === "move_in" ? "Move-In" : "Move-Out"} Inspection</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={() => setType("move_in")} className={type === "move_in" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"}>Move-In</button>
              <button type="button" onClick={() => setType("move_out")} className={type === "move_out" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"}>Move-Out</button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenant</span>
                  <select name="tenant_id" required className="input-modav mt-1">
                    <option value="">—</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit</span>
                  <select name="unit_id" required className="input-modav mt-1">
                    <option value="">—</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.unit_name}</option>)}
                  </select></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Inspection Date</span>
                  <input type="date" name="inspection_date" required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Overall Condition</span>
                  <select name="condition" className="input-modav mt-1">
                    <option value="excellent">Excellent</option><option value="good">Good</option>
                    <option value="fair">Fair</option><option value="poor">Poor</option>
                  </select></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Findings</span>
                <textarea name="findings" rows={2} className="input-modav mt-1" placeholder="Walls clean, fans working, etc."/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Meter Readings</span>
                <input name="meter_readings" className="input-modav mt-1" placeholder="Electric: 12345 / Water: 678"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">{type === "move_in" ? "Existing Damages" : "New Damages"}</span>
                <textarea name="damages" rows={2} className="input-modav mt-1"/></label>

              {type === "move_in" && (
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Keys Handed Over</span>
                  <input name="keys" className="input-modav mt-1" placeholder="2 main door, 1 gate, 1 mailbox"/></label>
              )}

              {type === "move_out" && (
                <div className="grid grid-cols-3 gap-3">
                  <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Repair Cost</span>
                    <input type="number" name="repair_cost" className="input-modav mt-1"/></label>
                  <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Caution Deduction</span>
                    <input type="number" name="caution_deduction" className="input-modav mt-1"/></label>
                  <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Refund Amount</span>
                    <input type="number" name="refund_amount" className="input-modav mt-1"/></label>
                </div>
              )}

              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Additional Notes</span>
                <textarea name="notes" rows={2} className="input-modav mt-1"/></label>

              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#E5EBE8]">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166] mb-2">Tenant Signature</div>
                  <SignaturePad onChange={setTenantSig} height={120}/>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166] mb-2">Manager Signature</div>
                  <SignaturePad onChange={setManagerSig} height={120}/>
                </div>
              </div>

              <button type="submit" className="btn-gold w-full justify-center" data-testid="save-inspection-btn">Save Inspection</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
